/**
 * SPJ-1723: standard unico per i campi che salvano un valore.
 *
 *   - Invio      -> salva
 *   - clic fuori -> salva
 *   - Esc        -> annulla (torna al valore salvato)
 *
 * Mentre si scrive il valore resta una bozza locale: niente salvataggio a ogni tasto. Il
 * salvataggio parte una volta sola e solo se il valore e' cambiato. `onCommit` riceve un
 * oggetto con la stessa forma dell'evento di `onChange` ({ target: { value } }), cosi' i
 * gestori esistenti si riusano senza riscriverli.
 */
import { useEffect, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';

export type CommitEventLike = { target: { value: string } };

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'defaultValue'> & {
  value: string | number | null | undefined;
  onCommit: (event: CommitEventLike) => void | Promise<void>;
};

const asText = (value: Props['value']) => (value === null || value === undefined ? '' : String(value));

export function CommitInput({ value, onCommit, onBlur, onKeyDown, onFocus, ...rest }: Props) {
  const [draft, setDraft] = useState(() => asText(value));
  const focusedRef = useRef(false);
  const cancelledRef = useRef(false);

  // Il valore salvato cambia da fuori (ricarica, altro campo): si allinea la bozza, ma non
  // mentre l'utente sta scrivendo.
  useEffect(() => {
    if (!focusedRef.current) setDraft(asText(value));
  }, [value]);

  const commit = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (draft !== asText(value)) void onCommit({ target: { value: draft } });
  };

  return (
    <input
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        focusedRef.current = true;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur(); // il salvataggio parte dal blur, una volta sola
        } else if (e.key === 'Escape') {
          cancelledRef.current = true;
          setDraft(asText(value));
          e.currentTarget.blur();
        }
        onKeyDown?.(e);
      }}
    />
  );
}

export default CommitInput;
